/**
 * One attention item (twin slice 1, plan W5 step 13).
 *
 * Every word is the item's own -- the selector's headline, lines, steps,
 * e-stop card, evidence line and status line -- or one of the structural
 * labels in `card-words.ts`. In order:
 *
 *   title and headline, with a band glyph (shape, not colour alone);
 *   where and since (`cardMetaLine`), or "Looks fixed, confirming…" /
 *   "Cleared at HH:MM" for a card that is no longer up;
 *   W6's Guests and Robots lines for a car; the other reports of a robot;
 *   who acts; the numbered steps, or the e-stop card's;
 *   the evidence line ("does Nexsoss know?");
 *   items folded under it, each with its stated join;
 *   ops Details, collapsed (a site audience's items carry none).
 *
 * A card that looks fixed is dimmed; so is a cleared one. Neither has
 * steps, an e-stop card, evidence or actions: there is nothing to do.
 * Dimming is not the only cue: the status line is part of the card's
 * accessible name, so one moving by card hears it too.
 *
 * Actions are the host's: "Show where" and "Open <name> panel", each a
 * 56 px button, offered only on a card that is up and about one robot, one
 * car or one door. A button is a tap the person makes; nothing here moves the
 * camera or opens a drawer by itself.
 */
import { IconAlertTriangle, IconCheck, IconInfo, IconX } from '../../../design-system/icons';
import { ATTENTION_LABELS, cardMetaLine } from '../../../state/attention/card-words';
import type { AttentionItem, AttentionSubject } from '../../../state/attention/select';
import type { SiteTimeZone } from '../../../state/attention/words';
import './attention.css';

export interface AttentionCardActions {
  onShowWhere?: (subject: AttentionSubject) => void;
  onOpenPanel?: (subject: AttentionSubject) => void;
}

export interface AttentionCardProps extends AttentionCardActions {
  item: AttentionItem;
  clock: SiteTimeZone;
  /** Nested under another card: smaller, no actions. */
  folded?: boolean;
}

function BandGlyph({ band }: { band: AttentionItem['band'] }) {
  const label = ATTENTION_LABELS.band[band];
  // An octagon-ish stop for safety, a triangle for blocked work, a circle
  // for upkeep.
  const icon =
    band === 'safety' ? (
      <IconX size={16} />
    ) : band === 'blocked' ? (
      <IconAlertTriangle size={16} />
    ) : (
      <IconInfo size={16} />
    );
  return (
    <span
      className={`nx-attention-card__band nx-attention-card__band--${band}`}
      role="img"
      aria-label={label}
    >
      {icon}
    </span>
  );
}

/** One robot, one car or one door: the only subjects a panel or a place
 *  belongs to. */
function single(subject: AttentionSubject): string | null {
  if (subject.kind === 'lift') return subject.lift;
  if (subject.kind === 'door') return subject.door;
  if (subject.kind === 'robot' && subject.robots.length === 1) return subject.robots[0]!.name;
  return null;
}

export function AttentionCard({
  item,
  clock,
  folded = false,
  onShowWhere,
  onOpenPanel,
}: AttentionCardProps) {
  const meta = cardMetaLine(item, clock);
  const up = item.status === 'active';
  const name = single(item.subject);
  const actions = up && !folded && name !== null;
  const panelLabel = name !== null ? ATTENTION_LABELS.openPanel(name) : null;
  const estop = item.estop;
  return (
    <article
      className={`nx-attention-card nx-attention-card--${item.status}${folded ? ' nx-attention-card--folded' : ''}`}
      data-status={item.status}
      data-band={item.band}
      data-group={item.group}
      aria-label={
        // A card that looks fixed or has cleared says so in its name: one
        // moving by card must not hear an item that sounds up.
        item.status === 'active' || item.statusLine === null
          ? `${item.title}: ${item.headline}`
          : `${item.title}: ${item.headline}. ${item.statusLine}`
      }
    >
      <header className="nx-attention-card__head">
        <BandGlyph band={item.band} />
        <div className="nx-attention-card__titles">
          <h4 className="nx-attention-card__title">{item.title}</h4>
          {item.headline !== item.title ? (
            <p className="nx-attention-card__headline">{item.headline}</p>
          ) : null}
        </div>
      </header>

      {meta ? (
        <p className="nx-attention-card__meta" data-testid="card-meta">
          {meta}
        </p>
      ) : null}

      {item.lines.length > 0 ? (
        <ul className="nx-attention-card__lines">
          {item.lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      ) : null}

      {item.also.length > 0 ? (
        <p className="nx-attention-card__also">
          {ATTENTION_LABELS.also} {item.also.join('; ')}
        </p>
      ) : null}

      {item.whoActsLine ? <p className="nx-attention-card__who">{item.whoActsLine}</p> : null}

      {estop ? (
        <div className="nx-attention-card__estop" data-kind={estop.kind}>
          {estop.heading ? (
            <p className="nx-attention-card__steps-heading">{estop.heading}</p>
          ) : null}
          {estop.notice ? <p className="nx-attention-card__notice">{estop.notice}</p> : null}
          {estop.jobLine ? <p className="nx-attention-card__job">{estop.jobLine}</p> : null}
          {estop.steps.length > 0 ? (
            <ol className="nx-attention-card__steps">
              {estop.steps.map((s) => (
                <li key={s.id} data-step={s.id} data-done={s.done ? 'true' : undefined}>
                  {s.done ? (
                    <span
                      className="nx-attention-card__done"
                      role="img"
                      aria-label={ATTENTION_LABELS.done}
                    >
                      <IconCheck size={14} />
                    </span>
                  ) : null}
                  {s.text}
                </li>
              ))}
            </ol>
          ) : null}
          {estop.closing ? <p className="nx-attention-card__closing">{estop.closing}</p> : null}
        </div>
      ) : item.steps.length > 0 ? (
        <div className="nx-attention-card__plain-steps">
          {item.stepsHeading ? (
            <p className="nx-attention-card__steps-heading">{item.stepsHeading}</p>
          ) : null}
          <ol className="nx-attention-card__steps">
            {item.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        </div>
      ) : null}

      {item.evidence ? (
        <p
          className={`nx-attention-card__evidence${item.evidence.pleaseCall ? ' nx-attention-card__evidence--call' : ''}`}
          data-tier={item.evidence.tier}
        >
          {item.evidence.text}
        </p>
      ) : null}

      {item.folded.length > 0 ? (
        <ul className="nx-attention-card__folded">
          {item.folded.map(({ item: inner, join }) => (
            <li key={inner.key}>
              <p className="nx-attention-card__join">{join}</p>
              <AttentionCard item={inner} clock={clock} folded />
            </li>
          ))}
        </ul>
      ) : null}

      {item.details.length > 0 ? (
        <details className="nx-attention-card__details">
          <summary>{ATTENTION_LABELS.details}</summary>
          <ul>
            {item.details.map((d, i) => (
              <li key={i}>
                <code>{d}</code>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {actions && (onShowWhere || onOpenPanel) ? (
        <div className="nx-attention-card__actions">
          {onShowWhere ? (
            <button
              type="button"
              className="nx-attention-card__action"
              onClick={() => onShowWhere(item.subject)}
            >
              {ATTENTION_LABELS.showWhere}
            </button>
          ) : null}
          {onOpenPanel ? (
            <button
              type="button"
              className="nx-attention-card__action"
              onClick={() => onOpenPanel(item.subject)}
            >
              {panelLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
